import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import PaymentGateway from '../components/PaymentGateway';
import { useStudyPlans } from '../hooks/useStudyPlans';
import { formatUsd } from '../data/studyPlans';
import {
  activateMockPaymentSubscription,
  extendOwnSubscription,
} from '../utils/subscriptions';
import { getAppNavItems } from '../utils/appNavItems';
import { SBA } from '../theme';

export default function BillingPage() {
  const { currentUser, isAdmin, hasSubscription, subscriptionInfo, refreshSubscription } = useAuth();
  const { plans, extensions, loading } = useStudyPlans();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedId, setSelectedId] = useState(location.state?.planId || '');
  const [step, setStep] = useState('choose'); // 'choose' | 'checkout'
  const [extDays, setExtDays] = useState(location.state?.extendDays || '');
  const [extStep, setExtStep] = useState(location.state?.extendDays ? 'checkout' : 'choose');

  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });

  const selected = useMemo(() => {
    if (!plans.length) return null;
    return plans.find((p) => p.id === selectedId) || plans[0];
  }, [plans, selectedId]);

  const selectedExtension = useMemo(
    () => extensions.find((o) => String(o.days) === String(extDays)) || null,
    [extensions, extDays]
  );

  async function handlePlanPay() {
    const result = await activateMockPaymentSubscription({
      user: currentUser,
      planId: selected.id,
      planDays: selected.days,
      planLabel: selected.name,
      amountPaid: selected.price,
    });
    await refreshSubscription(currentUser, isAdmin);
    navigate(`/payment-success?paymentId=${encodeURIComponent(result.paymentId)}`, {
      replace: true,
      state: { paymentId: result.paymentId, planLabel: selected.name },
    });
  }

  async function handleExtensionPay() {
    const result = await extendOwnSubscription({
      user: currentUser,
      days: selectedExtension.days,
      price: selectedExtension.price,
    });
    await refreshSubscription(currentUser, isAdmin);
    navigate(`/payment-success?paymentId=${encodeURIComponent(result.paymentId)}`, {
      replace: true,
      state: {
        paymentId: result.paymentId,
        planLabel: `${selectedExtension.days}-day extension`,
      },
    });
  }

  return (
    <AppShell navItems={navItems}>
      <div style={s.wrap}>
        <div style={s.hero}>
          <p style={s.eyebrow}>{hasSubscription ? 'My Plan' : 'Enroll'}</p>
          <h1 style={s.title}>
            {hasSubscription ? 'Your subscription' : 'Enroll — choose your AI study plan'}
          </h1>
          <p style={s.sub}>
            {hasSubscription
              ? 'Your plan is active. Need more time? Add extra days below.'
              : 'Select a 1, 2, or 3 month GED subscription, then continue to the payment gate to unlock your full student dashboard.'}
          </p>
        </div>

        {loading ? <p>Loading plans…</p> : null}

        {/* ---------- New subscription flow ---------- */}
        {!hasSubscription && step === 'choose' ? (
          <>
            <div style={s.planGrid}>
              {plans.map((plan) => {
                const active = (selected?.id || '') === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedId(plan.id)}
                    style={{
                      ...s.planCard,
                      borderColor: active ? SBA.gold : '#e2e8f0',
                      boxShadow: active
                        ? '0 12px 30px rgba(27,54,93,0.16)'
                        : s.planCard.boxShadow,
                      transform: active ? 'translateY(-2px)' : 'none',
                    }}
                  >
                    {plan.badge ? <span style={s.badge}>{plan.badge}</span> : null}
                    <h3 style={s.planName}>{plan.name}</h3>
                    <p style={s.duration}>{plan.durationLabel}</p>
                    <div style={s.priceRow}>
                      <span style={s.price}>{formatUsd(plan.price)}</span>
                      {plan.discountPercent > 0 ? (
                        <span style={s.was}>{formatUsd(plan.regularPrice)}</span>
                      ) : null}
                    </div>
                    {plan.discountPercent > 0 ? (
                      <p style={s.discount}>{plan.discountPercent}% discount applied</p>
                    ) : null}
                    <ul style={s.features}>
                      {(plan.features || []).map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                    {plan.description ? <p style={s.valueProp}>{plan.description}</p> : null}
                  </button>
                );
              })}
            </div>

            {selected ? (
              <button type="button" style={s.continueBtn} onClick={() => setStep('checkout')}>
                Continue to payment gate — {selected.name} · {formatUsd(selected.price)}
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                  arrow_forward
                </span>
              </button>
            ) : null}
          </>
        ) : null}

        {!hasSubscription && step === 'checkout' && selected ? (
          <PaymentGateway
            amount={selected.price}
            title="Payment gate"
            payLabel={`Pay ${formatUsd(selected.price)} & unlock dashboard`}
            summaryLines={[
              { label: 'Plan', value: selected.name },
              { label: 'Access', value: selected.durationLabel },
              ...(selected.discountPercent > 0
                ? [
                    { label: 'Regular price', value: formatUsd(selected.regularPrice) },
                    { label: 'Discount', value: `${selected.discountPercent}% off` },
                  ]
                : []),
            ]}
            onPay={handlePlanPay}
            onCancel={() => navigate('/payment-failed?reason=cancelled', { replace: true })}
            onFail={() => navigate('/payment-failed?reason=failed', { replace: true })}
          />
        ) : null}

        {/* ---------- Active subscription: extension flow ---------- */}
        {hasSubscription ? (
          <>
            <div style={s.activeBanner}>
              <div style={s.activeIcon}>
                <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#15803d' }}>
                  verified
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={s.activeTitle}>
                  {subscriptionInfo?.planLabel || 'Subscription active'}
                </p>
                <p style={s.activeSub}>
                  {subscriptionInfo?.daysLeft != null
                    ? `${subscriptionInfo.daysLeft} day${subscriptionInfo.daysLeft === 1 ? '' : 's'} of access remaining`
                    : 'Full dashboard access'}
                </p>
              </div>
              <button type="button" style={s.linkBtn} onClick={() => navigate('/dashboard')}>
                Go to dashboard
              </button>
            </div>

            {!isAdmin && extensions.length > 0 ? (
              extStep === 'choose' || !selectedExtension ? (
                <div style={s.extCard}>
                  <h2 style={s.extTitle}>Need more days?</h2>
                  <p style={s.extSub}>
                    Extend your current subscription — pick how many extra days you need.
                  </p>
                  <div style={s.extRow}>
                    <select
                      value={extDays}
                      onChange={(e) => setExtDays(e.target.value)}
                      style={s.extSelect}
                    >
                      <option value="">Select extra days</option>
                      {extensions.map((o) => (
                        <option key={o.days} value={o.days}>
                          {o.days} days more — {formatUsd(o.price)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={!selectedExtension}
                      onClick={() => setExtStep('checkout')}
                      style={s.extBtn(!selectedExtension)}
                    >
                      Continue to payment gate
                    </button>
                  </div>
                </div>
              ) : (
                <PaymentGateway
                  amount={selectedExtension.price}
                  title="Payment gate — extension"
                  payLabel={`Pay ${formatUsd(selectedExtension.price)} for ${selectedExtension.days} extra days`}
                  summaryLines={[
                    { label: 'Add-on', value: `${selectedExtension.days} extra days` },
                    {
                      label: 'Current plan',
                      value: subscriptionInfo?.planLabel || 'GED Subscription',
                    },
                    {
                      label: 'Days left now',
                      value: `${subscriptionInfo?.daysLeft ?? 0} days`,
                    },
                  ]}
                  onPay={handleExtensionPay}
                  onCancel={() => navigate('/payment-failed?reason=cancelled', { replace: true })}
                  onFail={() => navigate('/payment-failed?reason=failed', { replace: true })}
                />
              )
            ) : null}
          </>
        ) : null}
      </div>
    </AppShell>
  );
}

const s = {
  wrap: { maxWidth: 1100 },
  hero: { marginBottom: 28 },
  eyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: SBA.goldStrong,
    marginBottom: 8,
  },
  title: {
    fontFamily: SBA.fontDisplay,
    fontSize: 28,
    fontWeight: 800,
    color: SBA.navy,
    marginBottom: 8,
  },
  sub: { fontSize: 15, color: SBA.onSurfaceVariant, lineHeight: 1.55, maxWidth: 720 },
  planGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: 16,
    marginBottom: 20,
  },
  planCard: {
    textAlign: 'left',
    background: '#fff',
    border: '2px solid #e2e8f0',
    borderRadius: 18,
    padding: 20,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(27,54,93,0.06)',
    position: 'relative',
  },
  badge: {
    display: 'inline-block',
    background: SBA.gold,
    color: '#1a1405',
    fontSize: 11,
    fontWeight: 800,
    padding: '4px 10px',
    borderRadius: 999,
    marginBottom: 10,
  },
  planName: { margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: SBA.navy },
  duration: { margin: '0 0 12px', fontSize: 13, color: SBA.onSurfaceVariant },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 },
  price: { fontSize: 28, fontWeight: 800, color: SBA.navy },
  was: { fontSize: 14, color: '#94a3b8', textDecoration: 'line-through' },
  discount: { fontSize: 12, fontWeight: 700, color: SBA.tertiary, marginBottom: 10 },
  features: {
    margin: '0 0 12px',
    paddingLeft: 18,
    color: SBA.onSurfaceVariant,
    fontSize: 13,
    lineHeight: 1.7,
  },
  valueProp: {
    margin: 0,
    fontSize: 12,
    lineHeight: 1.55,
    color: SBA.goldStrong,
    fontWeight: 600,
    borderTop: '1px dashed #e2e8f0',
    paddingTop: 10,
  },
  continueBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '16px 20px',
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${SBA.navy} 0%, ${SBA.navyDeep} 100%)`,
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    cursor: 'pointer',
    marginBottom: 8,
  },
  activeBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
    padding: '16px 18px',
    borderRadius: 16,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    marginBottom: 20,
  },
  activeIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: '#dcfce7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeTitle: { margin: 0, fontWeight: 800, color: '#166534', fontSize: 14 },
  activeSub: { margin: 0, fontSize: 12, color: '#15803d' },
  linkBtn: {
    border: 'none',
    background: SBA.navy,
    color: '#fff',
    borderRadius: 10,
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  extCard: {
    background: '#fff',
    borderRadius: 18,
    border: '1px solid #e2e8f0',
    padding: 22,
    boxShadow: '0 8px 28px rgba(27,54,93,0.08)',
  },
  extTitle: { margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: SBA.navy },
  extSub: { margin: '0 0 16px', fontSize: 14, color: SBA.onSurfaceVariant },
  extRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  extSelect: {
    flex: '1 1 260px',
    padding: '12px 14px',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    background: '#fff',
  },
  extBtn: (disabled) => ({
    padding: '12px 20px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#94a3b8' : `linear-gradient(135deg, ${SBA.gold} 0%, ${SBA.goldStrong} 100%)`,
    color: '#1a1405',
    fontWeight: 800,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
